import {
  altKeyOnly,
  noModifierKeys,
  platformModifierKeyOnly,
  shiftKeyOnly
} from 'ol/events/condition';
import DragBox from 'ol/interaction/DragBox';
import PointerInteraction from 'ol/interaction/Pointer';
import { createOrUpdateFromCoordinates } from 'ol/extent';
import ImageState from 'ol/ImageState';
import MapBrowserEventType from 'ol/MapBrowserEventType';
import Translate from 'ol/interaction/Translate';

import { InteractionGroup } from 'cadenza/map/interactions/interaction-group';
import { SnapInteraction } from 'cadenza/map/interactions/snap-interaction';
import { ModifyInteraction } from 'cadenza/map/interactions/modify-interaction';
import { SelectOverlayInteraction } from 'cadenza/map/interactions/select-overlay-interaction';
import { createModifyPointStyle, getSelectStyle } from 'cadenza/map/style/select-styles';
import { HIT_TOLERANCE } from 'cadenza/map/map-constants';
import {
  SketchFeatureProperty as FeatureProperty
} from 'cadenza/map/classic-sketch-layer/sketch-feature-property';
import { TranslateByHandleInteraction } from 'cadenza/map/interactions/translate-by-handle-interaction';

export class SketchModifyInteractionGroup extends InteractionGroup {

  _select;

  constructor (layer, options = {}) {
    const source = layer.getSource();
    const featureFilter = feature => !feature.get(FeatureProperty.IS_UNSELECTABLE);
    const select = new SelectOverlayInteraction({
      condition: event => (event.type === MapBrowserEventType.CLICK),
      toggleCondition: platformModifierKeyOnly,
      hitTolerance: HIT_TOLERANCE,
      filter: featureFilter,
      getSelectStyle,
      layers: [ layer ]
    }, featuresAndLayers => featuresAndLayers
      .flatMap(([ hitFeature, hitLayer ]) => withOtherGroupFeatures(layer, [ hitFeature ])
        .map(groupFeature => [ groupFeature, hitLayer ])));
    const dragBoxSelect = createBoxSelectionInteraction(layer, select.getFeatures());
    const modify = createModifyInteraction(layer, select.getFeatures(), HIT_TOLERANCE, options.map);
    const immediateSelect = createImmediateSelectInteraction(layer, select.getFeatures(), HIT_TOLERANCE);
    const translate = createTranslateInteraction(select.getFeatures(), HIT_TOLERANCE, featureFilter);
    const snap = new SnapInteraction({ source });
    const interactions = [ select, dragBoxSelect, translate, modify, immediateSelect, snap ];
    if (options.withTranslateByHandle) {
      const translateByHandle = createTranslateByHandleInteraction(select.getFeatures(), HIT_TOLERANCE, featureFilter);
      interactions.push(translateByHandle);
    }
    super(...interactions);
    this._select = select;
  }

  getSelectedFeatures () {
    return this._select.getFeatures();
  }

}

function createBoxSelectionInteraction (layer, selectFeatureCollection) {
  const dragBox = new DragBox({
    condition: shiftKeyOnly
  });

  dragBox.on('boxend', () => {
    selectFeatureCollection.clear();
    const extent = dragBox.getGeometry().getExtent();
    let hitFeatures = [];
    layer.getSource().forEachFeatureIntersectingExtent(extent, feature => hitFeatures.push(feature));
    hitFeatures = withOtherGroupFeatures(layer, hitFeatures);
    hitFeatures = hitFeatures.filter(feature => !feature.get(FeatureProperty.IS_UNSELECTABLE));
    hitFeatures.forEach(feature => selectFeatureCollection.push(feature));
  });

  return dragBox;
}

// Creates an additional select interaction, which selects the features already on pointerdown.
// It allows the user to start modifying the geometry immediately, even if it wasn't selected previously.
function createImmediateSelectInteraction (layer, selectFeatureCollection, hitTolerance) {
  return new PointerInteraction({
    handleDownEvent: mapBrowserEvent => {
      if (noModifierKeys(mapBrowserEvent)) {
        const pixels = [
          mapBrowserEvent.pixel.map(value => value - hitTolerance / 2),
          mapBrowserEvent.pixel.map(value => value + hitTolerance / 2)
        ];
        const coordinates = pixels.map(pixel => mapBrowserEvent.map.getCoordinateFromPixel(pixel));
        const extent = createOrUpdateFromCoordinates(coordinates);

        let hitFeatures = [];
        layer.getSource().forEachFeatureIntersectingExtent(extent, feature => hitFeatures.push(feature));

        hitFeatures = hitFeatures.filter(feature => !feature.get(FeatureProperty.IS_UNSELECTABLE));

        if (hitFeatures.length > 0 && !hitFeatures.some(feature => selectFeatureCollection.getArray().indexOf(feature) >= 0)) {
          selectFeatureCollection.clear();
          const hitFeature = hitFeatures.pop();
          withOtherGroupFeatures(layer, [ hitFeature ]).forEach(feature => selectFeatureCollection.push(feature));
          mapBrowserEvent.immediateSelectionHandled = true;
          forceLayerRedraw(layer);
        }
      }
    }
  });
}

function forceLayerRedraw (layer) {
  const image = layer.getRenderer().image_;
  const imageState = image.getState();
  if (imageState !== ImageState.IDLE) {
    image.state = ImageState.IDLE;
    image.load();
  }
}

function createModifyInteraction (layer, features, hitTolerance, map) {
  return new ModifyInteraction({
    sketchLayer: layer,
    features,
    // Insert vertex only with ALT modifier pressed, so we are able to drag the whole line/polyline. Otherwise the modify interaction wins and adds new vertices instead of allowing to drag the geometry.
    insertVertexCondition: event => !event.immediateSelectionHandled && altKeyOnly(event),
    pixelTolerance: hitTolerance,
    style: createModifyPointStyle(),
    map
  });
}

function createTranslateByHandleInteraction (features, hitTolerance, featureFilter) {
  return new TranslateByHandleInteraction({
    features,
    hitTolerance,
    filter: featureFilter
  });
}

// Creates a geometry drag interaction. It enables dragging of any geometry by clicking and dragging any of its interior point.
function createTranslateInteraction (features, hitTolerance, featureFilter) {
  return new Translate({
    features,
    hitTolerance,
    filter: featureFilter
  });
}

// Returns new array containing all passed features and additionally all features in layer, which
// belong to the same groups as the passed features.
function withOtherGroupFeatures (layer, features) {
  if (!layer) {
    return features;
  }
  const groups = new Set();
  const featureIds = new Set();
  for (const feature of features) {
    const group = feature.get(FeatureProperty.GROUP);
    if (group) {
      groups.add(group);
    }
    featureIds.add(feature.getId());
  }
  const allFeatures = layer.getSource().getFeatures();
  const additionalFeatures = [];
  for (const feature of allFeatures) {
    const group = feature.get(FeatureProperty.GROUP);
    if (group && groups.has(group) && !featureIds.has(feature.getId())) {
      additionalFeatures.push(feature);
      featureIds.add(feature.getId());
    }
  }
  return features.concat(additionalFeatures);
}
